<template>
    <div class="container-fluid corporate-detail-page weeduc-admin-panel page-card text-left p-0">
        <header class="corporate-detail-header">
            <div class="corporate-detail-header__lead">
                <TitlePage
                    :title="user?.name || t.detail.title"
                    :subtitle="t.detail.subtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="corporate-detail-header__actions">
                <v-btn
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    :loading="exporting"
                    :disabled="loading || error || !user || exporting"
                    @click="exportPdf"
                >
                    <v-icon class="me-1" size="small">mdi-file-pdf-box</v-icon>
                    Exportar PDF
                </v-btn>
                <v-btn
                    variant="flat"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    :disabled="loading || error || !user"
                    @click="goEdit"
                >
                    <v-icon class="me-1" size="small">mdi-pencil</v-icon>
                    {{ t.collaborators.editCollaborator }}
                </v-btn>
                <v-btn
                    variant="outlined"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    :disabled="loading || error || !user || resettingPassword"
                    @click="resetPasswordOpen = true"
                >
                    <v-icon class="me-1" size="small">mdi-lock-reset</v-icon>
                    {{ t.detail.resetPassword }}
                </v-btn>
                <v-btn
                    variant="tonal"
                    color="error"
                    rounded="lg"
                    class="text-none"
                    :disabled="loading || error || !user || removing"
                    @click="removeOpen = true"
                >
                    <v-icon class="me-1" size="small">mdi-delete-outline</v-icon>
                    {{ t.collaborators.delete }}
                </v-btn>
                <v-btn
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    @click="$router.push('/dashboard/corporativo/colaboradores')"
                >
                    <v-icon class="me-1" size="small">mdi-arrow-left</v-icon>
                    {{ t.detail.back }}
                </v-btn>
            </div>
        </header>

        <v-dialog v-model="resetPasswordOpen" max-width="520">
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">{{ t.detail.resetPasswordTitle }}</h2>
                        <p class="corporate-dialog__subtitle">
                            {{
                                t.detail.resetPasswordConfirm.replace(
                                    '{name}',
                                    user?.name || 'este colaborador',
                                )
                            }}
                        </p>
                    </div>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <p class="corporate-dialog__hint mb-0">{{ t.detail.resetPasswordLead }}</p>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn
                        variant="text"
                        class="text-none"
                        :disabled="resettingPassword"
                        @click="resetPasswordOpen = false"
                    >
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="resettingPassword"
                        @click="confirmResetPassword"
                    >
                        {{ t.detail.confirm }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="removeOpen" max-width="480" persistent>
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <h2 class="corporate-dialog__title">{{ t.collaborators.delete }}</h2>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <p class="corporate-dialog__hint mb-0">{{ t.collaborators.deleteConfirm }}</p>
                    <p v-if="user?.name" class="corporate-dialog__user-line mt-3 mb-0">
                        {{ user.name }}
                    </p>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn
                        variant="text"
                        class="text-none"
                        :disabled="removing"
                        @click="closeRemove"
                    >
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="error"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="removing"
                        @click="executeRemove"
                    >
                        {{ t.collaborators.delete }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <div class="corporate-detail-shell">
            <div v-if="loading" class="corporate-detail-empty">
                <Loader :isActive="false" />
            </div>
            <div v-else-if="error" class="corporate-detail-empty">{{ t.common.error }}</div>

            <template v-else>
                <section class="corporate-detail-hero">
                    <div
                        class="corporate-detail-avatar"
                        :class="user.status === 'active' ? 'is-active' : 'is-inactive'"
                        aria-hidden="true"
                    >
                        {{ initials(user.name) }}
                    </div>
                    <div class="corporate-detail-hero__body">
                        <div class="corporate-detail-hero__title-row">
                            <h2>{{ user.name }}</h2>
                            <span
                                class="corporate-detail-chip"
                                :class="user.status === 'active' ? 'is-success' : 'is-muted'"
                            >
                                {{ user.status === 'active' ? t.collaborators.active : t.collaborators.inactive }}
                            </span>
                            <span
                                v-if="user.isResponsible"
                                class="corporate-detail-chip is-responsible"
                            >
                                {{ t.collaborators.responsible }}
                            </span>
                        </div>
                        <p class="corporate-detail-hero__email">{{ user.email }}</p>
                        <div class="corporate-detail-hero__meta">
                            <span>
                                <fa icon="fa-diagram-project" />
                                {{ user.sectorName || '—' }}
                            </span>
                            <span>
                                <fa icon="fa-id-badge" />
                                {{ user.roleName || '—' }}
                            </span>
                            <span v-if="user.managerName">
                                <fa icon="fa-user-tie" />
                                {{ user.managerName }}
                            </span>
                        </div>
                    </div>
                    <div class="corporate-detail-hero__progress">
                        <span>{{ t.detail.overallProgress }}</span>
                        <strong>{{ user.progressPercent || 0 }}%</strong>
                        <div class="corporate-detail-progress-track" aria-hidden="true">
                            <div
                                class="corporate-detail-progress-fill"
                                :class="progressTone(user.progressPercent)"
                                :style="{ width: `${Math.min(100, user.progressPercent || 0)}%` }"
                            />
                        </div>
                    </div>
                </section>

                <section class="corporate-detail-stats" :aria-label="t.common.eduSummaryAria">
                    <article class="corporate-detail-stat">
                        <span>{{ t.detail.courses }}</span>
                        <strong>{{ progress.totalAssigned || 0 }}</strong>
                    </article>
                    <article class="corporate-detail-stat">
                        <span>{{ t.detail.completed }}</span>
                        <strong>{{ progress.completed || 0 }}</strong>
                    </article>
                    <article class="corporate-detail-stat">
                        <span>{{ t.detail.overdue }}</span>
                        <strong>{{ progress.overdue || 0 }}</strong>
                    </article>
                    <article class="corporate-detail-stat">
                        <span>{{ t.detail.points }}</span>
                        <strong>{{ progress.gamificationPoints || 0 }}</strong>
                    </article>
                    <article class="corporate-detail-stat">
                        <span>{{ t.detail.level }}</span>
                        <strong class="corporate-detail-stat__level">{{ progress.gamificationLevel || '—' }}</strong>
                    </article>
                </section>

                <div class="corporate-detail-grid">
                    <section class="corporate-detail-panel">
                        <div class="corporate-detail-panel__head">
                            <h3>{{ t.detail.orgData }}</h3>
                            <p>{{ t.detail.orgLead }}</p>
                        </div>
                        <dl class="corporate-detail-fields">
                            <div>
                                <dt>{{ t.collaborators.name }}</dt>
                                <dd>{{ user.name }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.email }}</dt>
                                <dd>{{ user.email }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.sector }}</dt>
                                <dd>{{ user.sectorName || '—' }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.role }}</dt>
                                <dd>{{ user.roleName || '—' }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.manager }}</dt>
                                <dd>{{ user.managerName || '—' }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.admissionDate }}</dt>
                                <dd>{{ formatDate(user.admissionDate) }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.internalCode }}</dt>
                                <dd>{{ user.internalCode || '—' }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.status }}</dt>
                                <dd>
                                    <span
                                        class="corporate-detail-chip"
                                        :class="user.status === 'active' ? 'is-success' : 'is-muted'"
                                    >
                                        {{ user.status === 'active' ? t.collaborators.active : t.collaborators.inactive }}
                                    </span>
                                </dd>
                            </div>
                        </dl>
                    </section>

                    <section class="corporate-detail-panel">
                        <div class="corporate-detail-panel__head">
                            <h3>{{ t.detail.timeline }}</h3>
                            <p>{{ t.detail.timelineLead }}</p>
                        </div>
                        <div v-if="!timeline.length" class="corporate-detail-panel__empty">
                            {{ t.detail.timelineEmpty }}
                        </div>
                        <ol v-else class="corporate-detail-timeline">
                            <li v-for="event in timeline" :key="event.id">
                                <div class="corporate-detail-timeline__dot" aria-hidden="true" />
                                <div class="corporate-detail-timeline__body">
                                    <strong>{{ eventLabel(event) }}</strong>
                                    <p>{{ event.description }}</p>
                                    <time>{{ formatDateTime(event.createdAt) }}</time>
                                </div>
                            </li>
                        </ol>
                    </section>
                </div>

                <section class="corporate-detail-panel">
                    <div class="corporate-detail-panel__head">
                        <h3>{{ t.detail.courseHistory }}</h3>
                        <p>{{ t.detail.courseHistoryLead }}</p>
                    </div>

                    <div v-if="!(progress.enrolments || []).length" class="corporate-detail-panel__empty">
                        {{ t.detail.coursesEmpty }}
                    </div>

                    <div v-else class="corporate-detail-courses">
                        <article
                            v-for="(course, idx) in progress.enrolments"
                            :key="`${course.courseId || course.courseName}-${idx}`"
                            class="corporate-detail-course"
                        >
                            <div class="corporate-detail-course__main">
                                <div class="corporate-detail-course__title-row">
                                    <strong>{{ course.courseName }}</strong>
                                    <span
                                        class="corporate-detail-chip"
                                        :class="course.mandatory ? 'is-responsible' : 'is-muted'"
                                    >
                                        {{ course.mandatory ? t.coursesPage.mandatory : t.coursesPage.optional }}
                                    </span>
                                    <span
                                        class="corporate-detail-chip"
                                        :class="enrolmentStatusClass(course.status)"
                                    >
                                        {{ enrolmentStatusLabel(course.status) }}
                                    </span>
                                </div>
                                <div class="corporate-detail-course__meta">
                                    <span>{{ t.detail.source }}: {{ sourceLabel(course.source) }}</span>
                                </div>
                            </div>
                            <div class="corporate-detail-course__progress">
                                <div class="corporate-detail-course__progress-head">
                                    <span>{{ t.collaborators.progress }}</span>
                                    <strong>{{ course.progress || 0 }}%</strong>
                                </div>
                                <div class="corporate-detail-progress-track" aria-hidden="true">
                                    <div
                                        class="corporate-detail-progress-fill"
                                        :class="progressTone(course.progress)"
                                        :style="{ width: `${Math.min(100, course.progress || 0)}%` }"
                                    />
                                </div>
                            </div>
                        </article>
                    </div>
                </section>
            </template>
        </div>
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import usersApi from '@/utils/corporate/users';
import { exportCollaboratorPdf } from '@/utils/corporate/exportCollaboratorPdf';
import { corporateI18n } from '@/i18n/mixins';
import { mapState } from 'vuex';
import { toast } from 'vue3-toastify';

export default {
    name: 'CorporateCollaboratorDetail',
    mixins: [corporateI18n],
    components: { TitlePage, Loader },
    data() {
        return {
            loading: true,
            error: false,
            exporting: false,
            resetPasswordOpen: false,
            resettingPassword: false,
            removeOpen: false,
            removing: false,
            user: null,
            progress: {},
            timeline: [],
        };
    },
    computed: {
        ...mapState(['token']),
    },
    async created() {
        const id = this.$route.params.id;
        try {
            const user = await usersApi.getById(this.token, id);
            this.user = user;
            this.progress = user.progress || {};
            this.timeline = user.timeline || [];
        } catch (e) {
            console.error(e);
            this.error = true;
        } finally {
            this.loading = false;
        }
    },
    methods: {
        goEdit() {
            if (!this.user?.id) return;
            this.$router.push(`/dashboard/corporativo/colaboradores/${this.user.id}/editar`);
        },
        async exportPdf() {
            if (!this.user || this.exporting) return;
            this.exporting = true;
            try {
                await exportCollaboratorPdf({
                    user: this.user,
                    progress: this.progress,
                    timeline: this.timeline,
                    labels: this.t,
                });
            } catch (e) {
                console.error(e);
                alert('Não foi possível exportar o PDF do colaborador.');
            } finally {
                this.exporting = false;
            }
        },
        closeRemove() {
            if (this.removing) return;
            this.removeOpen = false;
        },
        async executeRemove() {
            if (!this.user?.id || this.removing) return;
            this.removing = true;
            try {
                await usersApi.remove(this.token, this.user.id);
                this.removeOpen = false;
                this.$router.push('/dashboard/corporativo/colaboradores');
            } catch (e) {
                console.error(e);
                const data = e?.response?.data;
                const message = (typeof data === 'string' && data.trim())
                    ? data
                    : (data?.message || e?.message || this.t.collaborators.deleteErrorTitle);
                toast.error(message);
            } finally {
                this.removing = false;
            }
        },
        async confirmResetPassword() {
            if (!this.user?.id || this.resettingPassword) return;
            this.resettingPassword = true;
            try {
                await usersApi.resetPassword(this.token, this.user.id);
                toast.success(this.t.detail.resetPasswordSuccess);
                this.resetPasswordOpen = false;
            } catch (e) {
                console.error(e);
                toast.error(this.t.detail.resetPasswordError);
            } finally {
                this.resettingPassword = false;
            }
        },
        initials(name) {
            if (!name) return '?';
            const parts = String(name).trim().split(/\s+/).filter(Boolean);
            if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        },
        progressTone(value) {
            const n = Number(value) || 0;
            if (n >= 80) return 'is-high';
            if (n >= 40) return 'is-medium';
            return 'is-low';
        },
        formatDate(value) {
            if (!value) return '—';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleDateString('pt-BR');
        },
        formatDateTime(value) {
            if (!value) return '—';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        eventLabel(event) {
            const map = {
                organization_updated: this.t.detail.eventOrgUpdated,
                course_completed: this.t.detail.eventCourseCompleted,
                course_in_progress: this.t.detail.eventCourseInProgress,
                course_overdue: this.t.detail.eventCourseOverdue,
                user_enrolled: this.t.detail.eventEnrolled,
            };
            return map[event.type] || event.type;
        },
        sourceLabel(source) {
            const map = {
                role: this.t.collaborators.role,
                sector: this.t.collaborators.sector,
                manual: this.t.detail.sourceManual,
                user: this.t.detail.sourceManual,
            };
            return map[source] || source || '—';
        },
        enrolmentStatusLabel(status) {
            const map = {
                completed: this.t.detail.statusCompleted,
                in_progress: this.t.detail.statusInProgress,
                overdue: this.t.detail.statusOverdue,
                not_started: this.t.detail.statusNotStarted,
            };
            return map[status] || status || '—';
        },
        enrolmentStatusClass(status) {
            if (status === 'completed') return 'is-success';
            if (status === 'overdue') return 'is-danger';
            if (status === 'in_progress') return 'is-responsible';
            return 'is-muted';
        },
    },
};
</script>

<style>
@import '@/css/weeduc/admin/corporate.css';
</style>

<style scoped>
.corporate-detail-page {
    background: #f4f5f8;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(37, 8, 145, 0.06);
    width: 100%;
}

.corporate-detail-header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem 1rem;
    background: linear-gradient(135deg, #fafbff 0%, #ffffff 100%);
    border-bottom: 1px solid #e8eaf0;
    border-radius: 16px 16px 0 0;
}

.corporate-detail-header__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
}

.corporate-detail-header__lead :deep(.titlelayoutGeneric) {
    border-bottom: none;
    padding-bottom: 0;
}

.corporate-detail-header__lead :deep(.titlelayoutGeneric h3) {
    font-size: 1.5rem;
    margin: 0 0 0.35rem;
    color: #1a1d26;
}

.corporate-detail-shell {
    padding: 1.25rem 1.5rem 1.5rem;
    height: calc(100vh - 210px);
    overflow-y: auto;
    scrollbar-width: none;
}

.corporate-detail-shell::-webkit-scrollbar {
    display: none;
}

.corporate-detail-empty {
    min-height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5c6370;
    position: relative;
}

.corporate-detail-hero {
    display: grid;
    grid-template-columns: auto 1fr minmax(180px, 220px);
    gap: 1.25rem;
    align-items: center;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 1.25rem 1.35rem;
    margin-bottom: 1.15rem;
}

.corporate-detail-avatar {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.05rem;
    font-weight: 700;
    flex-shrink: 0;
}

.corporate-detail-avatar.is-active {
    background: rgba(37, 8, 145, 0.1);
    color: #250891;
}

.corporate-detail-avatar.is-inactive {
    background: #eef0f3;
    color: #5c6370;
}

.corporate-detail-hero__body {
    min-width: 0;
    text-align: left;
}

.corporate-detail-hero__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.corporate-detail-hero__title-row h2 {
    margin: 0;
    font-size: 1.25rem;
    color: #1a1d26;
}

.corporate-detail-hero__email {
    margin: 0 0 0.55rem;
    color: #5c6370;
    font-size: 0.875rem;
}

.corporate-detail-hero__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem 1.1rem;
    font-size: 0.8rem;
    color: #3d4350;
}

.corporate-detail-hero__meta span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
}

.corporate-detail-hero__progress {
    text-align: left;
}

.corporate-detail-hero__progress > span {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #5c6370;
    margin-bottom: 0.2rem;
}

.corporate-detail-hero__progress strong {
    display: block;
    font-size: 1.5rem;
    color: #160266;
    margin-bottom: 0.45rem;
}

.corporate-detail-stats {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1.15rem;
}

.corporate-detail-stat {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 1rem 1.15rem;
    text-align: left;
}

.corporate-detail-stat span {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #5c6370;
    margin-bottom: 0.2rem;
}

.corporate-detail-stat strong {
    font-size: 1.35rem;
    color: #160266;
}

.corporate-detail-stat__level {
    font-size: 1.05rem !important;
}

.corporate-detail-grid {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 1rem;
    margin-bottom: 1rem;
}

.corporate-detail-panel {
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    padding: 1.15rem 1.25rem 1.35rem;
    margin-bottom: 1rem;
}

.corporate-detail-panel__head {
    margin-bottom: 1rem;
    text-align: left;
}

.corporate-detail-panel__head h3 {
    margin: 0 0 0.25rem;
    font-size: 1.05rem;
    color: #1a1d26;
}

.corporate-detail-panel__head p {
    margin: 0;
    font-size: 0.82rem;
    color: #5c6370;
}

.corporate-detail-panel__empty {
    min-height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5c6370;
    font-size: 0.875rem;
    text-align: center;
}

.corporate-detail-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem 1rem;
    margin: 0;
}

.corporate-detail-fields dt {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #5c6370;
    margin-bottom: 0.15rem;
}

.corporate-detail-fields dd {
    margin: 0;
    font-size: 0.9rem;
    color: #1a1d26;
    font-weight: 600;
}

.corporate-detail-timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.corporate-detail-timeline li {
    display: grid;
    grid-template-columns: 14px 1fr;
    gap: 0.75rem;
    position: relative;
}

.corporate-detail-timeline li:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 14px;
    bottom: -0.9rem;
    width: 2px;
    background: #e8eaf0;
}

.corporate-detail-timeline__dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: 0.3rem;
    background: #250891;
    box-shadow: 0 0 0 4px rgba(37, 8, 145, 0.12);
}

.corporate-detail-timeline__body {
    text-align: left;
}

.corporate-detail-timeline__body strong {
    display: block;
    font-size: 0.88rem;
    color: #1a1d26;
    margin-bottom: 0.15rem;
}

.corporate-detail-timeline__body p {
    margin: 0 0 0.2rem;
    font-size: 0.82rem;
    color: #3d4350;
}

.corporate-detail-timeline__body time {
    font-size: 0.72rem;
    color: #7a8294;
}

.corporate-detail-courses {
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
}

.corporate-detail-course {
    display: grid;
    grid-template-columns: 1.4fr 0.8fr;
    gap: 1rem;
    align-items: center;
    padding: 0.95rem 1.05rem;
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    background: #fafbfc;
}

.corporate-detail-course__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 0.35rem;
}

.corporate-detail-course__title-row strong {
    color: #1a1d26;
    font-size: 0.95rem;
}

.corporate-detail-course__meta {
    font-size: 0.78rem;
    color: #5c6370;
}

.corporate-detail-course__progress-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
    color: #5c6370;
}

.corporate-detail-course__progress-head strong {
    color: #160266;
    font-size: 0.85rem;
}

.corporate-detail-progress-track {
    height: 7px;
    border-radius: 999px;
    background: #e8eaf0;
    overflow: hidden;
}

.corporate-detail-progress-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.25s ease;
}

.corporate-detail-progress-fill.is-high {
    background: #1f9d63;
}

.corporate-detail-progress-fill.is-medium {
    background: #250891;
}

.corporate-detail-progress-fill.is-low {
    background: #c47a1a;
}

.corporate-detail-chip {
    display: inline-flex;
    align-items: center;
    font-size: 0.68rem;
    font-weight: 700;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
    white-space: nowrap;
}

.corporate-detail-chip.is-success {
    background: rgba(31, 157, 99, 0.12);
    color: #147a4a;
}

.corporate-detail-chip.is-muted {
    background: #eef0f3;
    color: #5c6370;
}

.corporate-detail-chip.is-responsible {
    background: rgba(37, 8, 145, 0.1);
    color: #250891;
}

.corporate-detail-chip.is-danger {
    background: rgba(196, 70, 70, 0.12);
    color: #a13333;
}

@media (max-width: 1100px) {
    .corporate-detail-hero,
    .corporate-detail-grid,
    .corporate-detail-course {
        grid-template-columns: 1fr;
    }

    .corporate-detail-stats {
        grid-template-columns: 1fr 1fr;
    }
}

@media (max-width: 640px) {
    .corporate-detail-stats,
    .corporate-detail-fields {
        grid-template-columns: 1fr;
    }
}

.dashboard.dark .corporate-detail-page {
    background: #1c1d24;
    box-shadow: none;
}

.dashboard.dark .corporate-detail-header {
    background: linear-gradient(135deg, #252730 0%, #201e26 100%);
    border-bottom-color: #2d2d2d;
}

.dashboard.dark .corporate-detail-header__lead :deep(.titlelayoutGeneric h3),
.dashboard.dark .corporate-detail-hero__title-row h2,
.dashboard.dark .corporate-detail-panel__head h3,
.dashboard.dark .corporate-detail-course__title-row strong,
.dashboard.dark .corporate-detail-fields dt {
    color: #f1f3f7;
}

.dashboard.dark .corporate-detail-hero,
.dashboard.dark .corporate-detail-stat,
.dashboard.dark .corporate-detail-panel {
    background: #252730;
    border-color: #353945;
}

.dashboard.dark .corporate-detail-avatar.is-active {
    background: rgba(165, 180, 252, 0.15);
    color: #c4b5fd;
}

.dashboard.dark .corporate-detail-avatar.is-inactive {
    background: #2a2e3a;
    color: #9ca3b4;
}

.dashboard.dark .corporate-detail-hero__email,
.dashboard.dark .corporate-detail-hero__progress > span,
.dashboard.dark .corporate-detail-stat span,
.dashboard.dark .corporate-detail-panel__head p,
.dashboard.dark .corporate-detail-course__meta,
.dashboard.dark .corporate-detail-course__progress-head,
.dashboard.dark .corporate-detail-fields dd,
.dashboard.dark .corporate-detail-empty {
    color: #9ca3b4;
}

.dashboard.dark .corporate-detail-hero__progress strong,
.dashboard.dark .corporate-detail-stat strong,
.dashboard.dark .corporate-detail-course__progress-head strong {
    color: #e8eaf0;
}

.dashboard.dark .corporate-detail-course {
    background: #201e26;
    border-color: #353945;
}

.dashboard.dark .corporate-detail-progress-track {
    background: #353945;
}

.dashboard.dark .corporate-detail-progress-fill.is-medium {
    background: #818cf8;
}

.dashboard.dark .corporate-detail-chip.is-muted {
    background: #2a2e3a;
    color: #9ca3b4;
}

.dashboard.dark .corporate-detail-chip.is-success {
    background: rgba(31, 157, 99, 0.2);
    color: #6ee7b7;
}

.dashboard.dark .corporate-detail-chip.is-responsible {
    background: rgba(165, 180, 252, 0.18);
    color: #c4b5fd;
}

.dashboard.dark .corporate-detail-chip.is-danger {
    background: rgba(196, 70, 70, 0.2);
    color: #fca5a5;
}

.dashboard.dark .corporate-detail-header__actions :deep(.v-btn--variant-tonal),
.dashboard.dark .corporate-detail-header__actions :deep(.v-btn--variant-outlined) {
    color: #e8eaf0 !important;
    background: rgba(255, 255, 255, 0.06) !important;
    border-color: #3d4150;
}
</style>
